import React from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { NoSsr } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import { isAuthenticatedState } from '../../../api/appState';
import { ProductDetailProduct } from '../../../api/products/productDetail';
import { activeFavoriteProducts } from '../../../config';
import ProductDetailCanceledStatus from '../../../components/ProductDetailCanceledStatus';
import { isProductCanceled } from '../../../helpers/isProductCanceled';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailFavoriteToggle from './ProductDetailFavoriteToggle';

export interface ProductDetailNameProps extends StandardDivProps<ProductDetailNameClassKey> {
  product: Pick<ProductDetailProduct, 'id' | 'name' | 'taxonomies'>;
  withFavoriteToggle?: boolean;
}

export type ProductDetailNameClassKey =
  | 'root'
  | 'name'
  | 'nameSection'
  | 'crossedText'
  | 'highlight'
  | 'favoriteToggle';

const useStyles = makeStyles((theme: Theme) =>
  createStyles<ProductDetailNameClassKey, {}>({
    root: {
      display: 'flex',
    },
    favoriteToggle: {
      alignSelf: 'flex-start',
    },
    name: {
      ...theme.typography.h1,
      fontSize: '2.2rem',
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
      margin: 0,
      padding: theme.spacing(1, 0),
      [theme.breakpoints.down('md')]: {
        fontSize: '1.9rem',
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.65rem',
      },
    },
    nameSection: {
      display: 'flex',
      flexDirection: 'column',
    },
    crossedText: {
      textDecoration: 'line-through',
    },
    highlight: {
      color: theme.palette.error.main,
    },
  }),
);

const ProductDetailName: React.FC<ProductDetailNameProps> = React.forwardRef(
  ({ className, product, withFavoriteToggle = false, ...rest }, ref) => {
    const classes = useStyles();
    const t = useTranslateWithFallback();
    const isAuthenticated = useRecoilValue(isAuthenticatedState);

    const productIsCanceled = isProductCanceled(product.taxonomies);

    return (
      <div ref={ref} className={clsx(classes.root, className)} {...rest}>
        <div className={classes.nameSection}>
          <h1
            className={clsx(classes.name, {
              [classes.crossedText]: productIsCanceled,
            })}
          >
            {t(product.name, { acceptString: true })}
          </h1>
          {productIsCanceled && <ProductDetailCanceledStatus />}
        </div>
        {activeFavoriteProducts && isAuthenticated && withFavoriteToggle && (
          <NoSsr>
            <ProductDetailFavoriteToggle className={classes.favoriteToggle} productId={product.id} />
          </NoSsr>
        )}
      </div>
    );
  },
);

ProductDetailName.displayName = 'ProductDetailName';

export default ProductDetailName;
